// German for the standing queue (tab-proposals). Registered on load.
// Queue vocabulary: claim → Behauptung, unheld → offen, hold/held → übernehmen/übernommen,
// disposition → Entscheid, disposed → entschieden, run → Lauf (aligned with the glossary).
import { registerDe } from '../i18n/index.js';

registerDe({
  // ── masthead / page states ─────────────────────────────────────────────────
  'Proposals': 'Vorschläge',
  'No engagement selected': 'Kein Auftrag ausgewählt',
  'The review queue is scoped to one engagement — every claim in it names a run, a document and a register it would enter.':
    'Die Prüf-Warteschlange gilt für einen Auftrag — jede Behauptung darin nennt einen Lauf, ein Dokument und ein Register, in das sie einginge.',
  'Every claim the agent has filed in': 'Jede Behauptung, die der Agent in',
  'carries a human disposition — the register is answerable for all of it.':
    'erfasst hat, trägt einen menschlichen Entscheid — das Register kann für alles einstehen.',
  'claims the agent has made in': 'Behauptungen hat der Agent in',
  'that nobody has stood behind. None of it is in the register yet.':
    'erfasst, hinter denen noch niemand steht. Nichts davon ist bisher im Register.',
  'Gate': 'Gate',
  'waits on this queue:': 'wartet auf diese Warteschlange:',
  'j/k · a hold · e amend · r refuse · d duplicate · u undo': 'j/k · a übernehmen · e anpassen · r ablehnen · d Duplikat · u rückgängig',
  'Refresh': 'Aktualisieren',

  // ── the rail's five cells ──────────────────────────────────────────────────
  'UNHELD': 'OFFEN',
  'COLLIDING': 'KOLLIDIEREND',
  'UNSOURCED': 'OHNE BELEG',
  'FLAGGED': 'MARKIERT',
  'SESSION': 'SITZUNG',
  'claims': 'Behauptungen',
  'every claim has a disposition': 'jede Behauptung ist entschieden',
  '{n} run': '{n} Lauf',
  '{n} runs': '{n} Läufe',
  'oldest {date}': 'älteste {date}',
  'not attributed to a run': 'keinem Lauf zugeordnet',
  'with the record': 'mit der Akte',
  'the comparison did not answer': 'der Vergleich hat nicht geantwortet',
  'nothing left to compare': 'nichts mehr zu vergleichen',
  '≥ {sim} to a register row': '≥ {sim} zu einer Registerzeile',
  'no evidence': 'kein Beleg',
  'nothing unheld': 'nichts mehr offen',
  'judged against the register instead': 'stattdessen am Register gemessen',
  'lint / low conf': 'Lint / niedr. Konf.',
  'lint error or confidence < 0.80': 'Lint-Fehler oder Konfidenz < 0.80',
  '{done} of {total}': '{done} von {total}',
  '{n} held': '{n} übernommen',
  '{n} refused': '{n} abgelehnt',
  'no queue to work': 'keine Warteschlange zu bearbeiten',
  'nothing disposed yet': 'noch nichts entschieden',

  // ── zone 1 · the queue ─────────────────────────────────────────────────────
  'The queue': 'Die Warteschlange',
  'reading': 'wird gelesen',
  'nothing to review': 'nichts zu prüfen',
  '{n} claim unheld': '{n} Behauptung offen',
  '{n} claims unheld': '{n} Behauptungen offen',
  '{n} disposed this session': '{n} in dieser Sitzung entschieden',
  'The register comparison': 'Der Registervergleich',
  'Register comparison unavailable': 'Registervergleich nicht verfügbar',
  'Every claim below is unchecked against the record. Holding one still writes; it just writes without the duplicate and contradiction check. The queue does not pretend the check passed.':
    'Jede Behauptung unten ist ungeprüft gegen die Akte. Eine Übernahme schreibt trotzdem — nur ohne die Duplikat- und Widerspruchsprüfung. Die Warteschlange tut nicht so, als wäre die Prüfung bestanden.',
  'run {id}': 'Lauf {id}',
  'the tool layer pushed back': 'die Tool-Ebene hat bei diesem Lauf',
  'times on this run': 'Mal zurückgewiesen',
  ', and it cited {covered} of the {n} document it was given': ', und er zitierte {covered} des {n} erhaltenen Dokuments',
  ', and it cited {covered} of the {n} documents it was given': ', und er zitierte {covered} von {n} erhaltenen Dokumenten',
  'Read the evidence closely.': 'Lesen Sie die Belege genau.',
  'Scoped to run {run}': 'Auf Lauf {run} eingegrenzt',
  'You arrived here from a link that named one run.': 'Sie kamen über einen Link, der einen Lauf benannte.',
  'Show every run': 'Alle Läufe anzeigen',
  'The review queue could not be read': 'Die Prüf-Warteschlange konnte nicht gelesen werden',
  'reading the queue': 'Warteschlange wird gelesen',
  'Nothing unheld.': 'Nichts offen.',
  'Nothing filed yet.': 'Noch nichts erfasst.',
  'This engagement has filed': 'Dieser Auftrag hat',
  'claims. Every one carries a human disposition:': 'Behauptungen erfasst. Jede trägt einen menschlichen Entscheid:',
  'held,': 'übernommen,',
  'amended before holding,': 'vor der Übernahme angepasst,',
  'refused. The register is answerable for all of them.': 'abgelehnt. Das Register kann für alle einstehen.',
  'No claim has been filed for this engagement yet. An extraction pass reads the documents in scope and files what it finds here — nothing reaches the register until you hold it.':
    'Für diesen Auftrag wurde noch keine Behauptung erfasst. Ein Extraktionslauf liest die Dokumente im Umfang und reicht hier ein, was er findet — nichts erreicht das Register, bevor Sie es übernehmen.',
  'asks for': 'verlangt',
  'and': 'und',
  'This half of it is met.': 'Diese Hälfte davon ist erfüllt.',
  'It is not met yet.': 'Es ist noch nicht erfüllt.',
  'Run an extraction pass →': 'Extraktionslauf starten →',
  'Open the register': 'Register öffnen',
  'No claim matches this filter': 'Keine Behauptung entspricht diesem Filter',
  'The queue is scoped to run {run}, and that run filed nothing.': 'Die Warteschlange ist auf Lauf {run} eingegrenzt, und dieser Lauf hat nichts eingereicht.',

  // ── the queue rail (tabs, sort, rows) ──────────────────────────────────────
  'Claims': 'Behauptungen',
  'untreated': 'unbehandelt',
  'in thread': 'im Thread',
  'nothing held in a thread': 'nichts in einem Thread gehalten',
  'treated': 'behandelt',
  'Sort the rail': 'Leiste sortieren',
  'sort': 'Sortierung',
  'run': 'Lauf',
  'type': 'Typ',
  'weight': 'Gewicht',
  'category': 'Kategorie',
  'kind': 'Art',
  'nothing untreated': 'nichts unbehandelt',
  'nothing treated yet': 'noch nichts behandelt',
  '{left} of {total} unheld': '{left} von {total} offen',
  'type, weight, category': 'Typ, Gewicht, Kategorie',
  'unattributed': 'nicht zugeordnet',
  'no type': 'ohne Typ',
  'no weight': 'ohne Gewicht',
  'uncategorised': 'ohne Kategorie',

  // ── kinds, types, weights, statuses ────────────────────────────────────────
  'requirement': 'Anforderung',
  'revision': 'Revision',
  'merge': 'Zusammenführung',
  'conflict': 'Konflikt',
  'deliverable': 'Arbeitsergebnis',
  'content item': 'Inhaltselement',
  'criterion': 'Kriterium',
  'test case': 'Testfall',
  'question': 'Frage',
  'functional': 'funktional',
  'non-functional': 'nicht-funktional',
  'non_functional': 'nicht-funktional',
  'constraint': 'Rahmenbedingung',
  'assumption': 'Annahme',
  'exclusion': 'Ausschluss',
  'out_of_scope': 'ausserhalb des Umfangs',
  'must': 'Muss',
  'should': 'Soll',
  'could': 'Kann',
  "won't": 'Nicht',
  'wont': 'Nicht',
  // register/deliverable row statuses (identical to the PortalAdminPage catalogue, so the
  // merged dictionary never disagrees with itself)
  'draft': 'Entwurf',
  'pending': 'ausstehend',
  'proposed': 'vorgeschlagen',
  'held': 'übernommen',
  'confirmed': 'bestätigt',
  'descoped': 'aus dem Umfang genommen',
  'baselined': 'in der Baseline',
  'superseded': 'ersetzt',
  'planned': 'geplant',
  'in_progress': 'in Arbeit',
  'delivered': 'geliefert',
  'accepted': 'abgenommen',
  'dropped': 'verworfen',
  // the page's disposition words
  'amended': 'angepasst',
  'refused': 'abgelehnt',
  'amended · held': 'angepasst · übernommen',
  // run statuses
  'queued': 'in Warteschlange',
  'starting': 'startet',
  'running': 'läuft',
  'done': 'abgeschlossen',
  'error': 'fehlgeschlagen',
  'aborted': 'abgebrochen',
  'timeout': 'Zeitüberschreitung',
  'running · will file more': 'läuft · reicht weitere ein',

  // ── flags ──────────────────────────────────────────────────────────────────
  'register comparison unavailable': 'Registervergleich nicht verfügbar',
  'twin': 'Zwilling',
  'twin of #{id} in this same run': 'Zwilling von #{id} im selben Lauf',
  'collides': 'kollidiert',
  'collides with {ref}': 'kollidiert mit {ref}',
  'lint': 'Lint',
  'lint error': 'Lint-Fehler',
  'no ev': 'kein Beleg',
  'warn': 'Warnung',
  '{n} lint warning': '{n} Lint-Warnung',
  '{n} lint warnings': '{n} Lint-Warnungen',
  'low conf': 'niedr. Konf.',
  'agent confidence {n}': 'Agent-Konfidenz {n}',
  'near': 'nah',
  'nearest {ref} at {sim}': 'nächstgelegen {ref} bei {sim}',

  // ── the stand ──────────────────────────────────────────────────────────────
  'filed by': 'erfasst von',
  'no run': 'kein Lauf',
  ' on ': ' am ',
  '{i} of {total}': '{i} von {total}',
  'The two things the record says': 'Die zwei Dinge, die die Akte sagt',
  'the later document is {ref}, {date}': 'das spätere Dokument ist {ref}, {date}',
  'Position A': 'Position A',
  'Position B': 'Position B',
  'this side wins': 'diese Seite gewinnt',
  'let this side win': 'diese Seite gewinnen lassen',
  'Hinges on {x}.': 'Hängt an {x}.',
  'The anchor was matched automatically — check it before resolving, because the resolution rewrites whatever it points at.':
    'Der Anker wurde automatisch zugeordnet — prüfen Sie ihn vor der Auflösung, denn die Auflösung überschreibt, worauf er zeigt.',
  'Deliverable': 'Arbeitsergebnis',
  'Definition of done — the sentence acceptance is judged against': 'Abnahmekriterien — die Sätze, an denen die Abnahme gemessen wird',
  '{n} requirement would be linked to it.': '{n} Anforderung würde damit verknüpft.',
  '{n} requirements would be linked to it.': '{n} Anforderungen würden damit verknüpft.',
  'Acceptance criterion for': 'Abnahmekriterium für',
  'Measurement method': 'Messmethode',
  'Test case': 'Testfall',
  'Preconditions:': 'Vorbedingungen:',
  'Expected result': 'Erwartetes Ergebnis',
  'The question that would go on the client-call agenda': 'Die Frage, die auf die Agenda des Kundengesprächs käme',
  'The revision that would enter {target}': 'Die Revision, die in {target} einginge',
  'the register': 'das Register',
  'the previous revision is kept, never overwritten': 'die vorherige Revision bleibt erhalten, wird nie überschrieben',
  'type {from} → {to}': 'Typ {from} → {to}',
  'priority {from} → {to}': 'Priorität {from} → {to}',
  'now {status}': 'jetzt {status}',
  'The statement that would enter the register': 'Die Aussage, die ins Register einginge',
  'Linter': 'Linter',
  'no blocking error': 'kein blockierender Fehler',
  'one error blocks a testable criterion': 'ein Fehler blockiert ein prüfbares Kriterium',
  'warning': 'Warnung',
  'pass': 'bestanden',
  'Observable subject, observable outcome, a threshold a tester can check.':
    'Beobachtbares Subjekt, beobachtbares Ergebnis, ein Schwellenwert, den ein Tester prüfen kann.',
  'What the record already says': 'Was die Akte bereits sagt',
  'unavailable': 'nicht verfügbar',
  'the row this resolution rewrites': 'die Zeile, die diese Auflösung umschreibt',
  'the requirement this criterion hangs from': 'die Anforderung, an der dieses Kriterium hängt',
  'not re-run for a disposed claim': 'für eine entschiedene Behauptung nicht erneut ausgeführt',
  'trigram over the deliverable names': 'Trigramm über die Arbeitsergebnis-Namen',
  'trigram over the register · pg_trgm': 'Trigramm über das Register · pg_trgm',
  'nothing to compare it against': 'nichts, womit sie sich vergleichen liesse',
  'register comparison': 'Registervergleich',
  'did not answer': 'hat nicht geantwortet',
  'This claim has': 'Diese Behauptung wurde',
  'NOT': 'NICHT',
  'been checked against the record. The page says so rather than showing an empty result that reads like a clean bill.':
    'gegen die Akte geprüft. Die Seite sagt es, statt ein leeres Ergebnis zu zeigen, das wie ein sauberer Befund wirkt.',
  '{n} criterion already': 'bereits {n} Kriterium',
  '{n} criteria already': 'bereits {n} Kriterien',
  'no criteria yet': 'noch keine Kriterien',
  'A criterion is measured against its own requirement and its sibling criteria — never against the register at large, where the parent always scores highest and tells you nothing.':
    'Ein Kriterium wird an seiner eigenen Anforderung und seinen Geschwisterkriterien gemessen — nie am Register insgesamt, wo der Elternteil immer am höchsten punktet und nichts verrät.',
  'Open {ref} →': '{ref} öffnen →',
  'anchor matched automatically': 'Anker automatisch zugeordnet',
  'anchored by the operator': 'vom Operator verankert',
  'what {ref} would say once position {side} wins': 'was {ref} sagen würde, sobald Position {side} gewinnt',
  'filed by the same run': 'vom selben Lauf erfasst',
  ', {s} s later': ', {s} s später',
  ', {s} s earlier': ', {s} s früher',
  'similarity': 'Ähnlichkeit',
  'Refuse — the run said it twice': 'Ablehnen — der Lauf hat es zweimal gesagt',
  'Stand on #{id} →': 'Zu #{id} wechseln →',
  'Two claims one run filed minutes apart can both be held, and were: a register reference was created and deleted fifteen minutes later — burned for good, because references are never reused.':
    'Zwei Behauptungen, die ein Lauf im Abstand von Minuten erfasste, können beide übernommen werden — und wurden es: eine Registerreferenz wurde erstellt und fünfzehn Minuten später gelöscht — für immer verbrannt, denn Referenzen werden nie wiederverwendet.',
  '— under the 0.60 line': '— unter der 0.60-Linie',
  'The names all but match. What differs is the sentence acceptance is judged against:':
    'Die Namen stimmen fast überein. Was sich unterscheidet, ist der Satz, an dem die Abnahme gemessen wird:',
  'Refuse — duplicate of {ref}': 'Ablehnen — Duplikat von {ref}',
  'Refuse — contradicts {ref}': 'Ablehnen — widerspricht {ref}',
  'next closest': 'nächstgelegene',
  '— nothing near': '— nichts in der Nähe',
  'Nothing in the register scores above': 'Nichts im Register erreicht mehr als',
  '— this claim is new ground. The trigram catches lexical near-twins and misses semantic ones, so this is a clean filter, not a clean bill.':
    '— diese Behauptung ist Neuland. Das Trigramm fängt lexikalische Beinahe-Zwillinge und übersieht semantische; dies ist also ein sauberer Filter, kein sauberer Befund.',
  'Disposed — the register comparison runs for standing claims only, and is not re-run here.':
    'Entschieden — der Registervergleich läuft nur für stehende Behauptungen und wird hier nicht erneut ausgeführt.',
  'Evidence': 'Belege',
  '{n} span': '{n} Stelle',
  '{n} spans': '{n} Stellen',
  'view span': 'Stelle ansehen',
  'conf {n}': 'Konf. {n}',
  'None attached —': 'Keine angehängt —',
  'a deliverable is derived from the requirements it claims to satisfy, not from a document. Judge it against the register above.':
    'ein Arbeitsergebnis leitet sich aus den Anforderungen ab, die es zu erfüllen behauptet, nicht aus einem Dokument. Messen Sie es am Register oben.',
  'a criterion is derived from its requirement, not from a document. It inherits {ref}’s evidence.':
    'ein Kriterium leitet sich aus seiner Anforderung ab, nicht aus einem Dokument. Es erbt die Belege von {ref}.',
  'its parent': 'seiner übergeordneten Anforderung',
  'this claim rests on nothing you can check. That alone is a reason to refuse it.':
    'diese Behauptung stützt sich auf nichts Prüfbares. Das allein ist ein Grund, sie abzulehnen.',
  'Why the agent proposed it': 'Warum der Agent es vorgeschlagen hat',
  'Reviewer note': 'Prüfernotiz',
  'Refused': 'Abgelehnt',
  'Held': 'Übernommen',
  'no register row was written. Reason recorded:': 'keine Registerzeile wurde geschrieben. Erfasster Grund:',
  'written to the register as': 'ins Register geschrieben als',
  '· amended before holding': '· vor der Übernahme angepasst',
  'Undo': 'Rückgängig',
  'Walk the refusal back — the claim stands unheld again': 'Die Ablehnung zurücknehmen — die Behauptung steht wieder offen',
  'Revert the hold — deletes the row it created and puts the claim back in the queue':
    'Die Übernahme zurücknehmen — löscht die erstellte Zeile und stellt die Behauptung zurück in die Warteschlange',
  'Un-refuse — back to the queue': 'Ablehnung zurücknehmen — zurück in die Warteschlange',
  'Un-hold — back to the queue': 'Übernahme zurücknehmen — zurück in die Warteschlange',
  'Holding this writes': 'Die Übernahme schreibt',
  '. Nothing else moves.': '. Sonst bewegt sich nichts.',

  // ── what the hold writes ───────────────────────────────────────────────────
  'a draft requirement, its revision 1 and {links}': 'eine Entwurfsanforderung, ihre Revision 1 und {links}',
  '{n} evidence link': '{n} Belegverknüpfung',
  '{n} evidence links': '{n} Belegverknüpfungen',
  'no evidence link': 'keine Belegverknüpfung',
  'a deliverable and {links}': 'ein Arbeitsergebnis und {links}',
  '{n} requirement link': '{n} Anforderungsverknüpfung',
  '{n} requirement links': '{n} Anforderungsverknüpfungen',
  'an acceptance criterion under {ref}': 'ein Abnahmekriterium unter {ref}',
  'its requirement': 'seiner Anforderung',
  'a test case and {links}': 'ein Testfall und {links}',
  '{n} criterion link': '{n} Kriteriumsverknüpfung',
  '{n} criterion links': '{n} Kriteriumsverknüpfungen',
  'a new revision of {ref} — the previous one is kept, never overwritten': 'eine neue Revision von {ref} — die vorherige bleibt erhalten, wird nie überschrieben',
  'the anchored requirement': 'der verankerten Anforderung',
  'a new revision of {ref} carrying the winning side': 'eine neue Revision von {ref} mit der siegreichen Position',
  'a draft requirement carrying the winning side': 'eine Entwurfsanforderung mit der siegreichen Position',
  'an open question on the client-call agenda': 'eine offene Frage auf der Agenda des Kundengesprächs',
  'a register row': 'eine Registerzeile',

  // ── the action foot and its panels ─────────────────────────────────────────
  'Hold — position {side} wins': 'Übernehmen — Position {side} gewinnt',
  'Hold it — pick a side': 'Übernehmen — Seite wählen',
  'Hold it': 'Übernehmen',
  'No side wins yet: both positions land as an open question on the Register page — your client-call agenda':
    'Noch gewinnt keine Seite: beide Positionen landen als offene Frage auf der Register-Seite — Ihrer Agenda für das Kundengespräch',
  'Log it as an open question': 'Als offene Frage festhalten',
  'Amend, then hold': 'Anpassen, dann übernehmen',
  'Hold exactly as proposed AND put it on the client-call agenda as an open question to refine':
    'Genau wie vorgeschlagen übernehmen UND als offene Frage zum Verfeinern auf die Agenda des Kundengesprächs setzen',
  'Hold · and put to the client': 'Übernehmen · und dem Kunden vorlegen',
  'Refuse': 'Ablehnen',
  'Remove this machine proposal outright — no refusal record, the audit entry keeps its payload. A claim worth an answer is refused instead.':
    'Diesen Maschinenvorschlag vollständig entfernen — kein Ablehnungseintrag, der Prüfpfad behält die Nutzdaten. Eine Behauptung, die eine Antwort verdient, wird stattdessen abgelehnt.',
  'Delete…': 'Löschen…',
  'Position {side} wins. It is applied as a NEW REVISION of the anchored requirement — revisions are append-only, so the losing position stays preserved on this proposal.':
    'Position {side} gewinnt. Sie wird als NEUE REVISION der verankerten Anforderung angewendet — Revisionen sind nur anfügbar, die unterlegene Position bleibt auf diesem Vorschlag erhalten.',
  '— none fits: create a NEW requirement from the winning position': '— keine passt: NEUE Anforderung aus der siegreichen Position erstellen',
  'statement override (optional) — leave empty to take the winning side verbatim':
    'Aussage überschreiben (optional) — leer lassen, um die siegreiche Seite wörtlich zu übernehmen',
  'why you resolved it this way (recorded on the proposal)': 'warum Sie es so aufgelöst haben (auf dem Vorschlag erfasst)',
  'Amend, then hold — the amendment is recorded as': 'Anpassen, dann übernehmen — die Anpassung wird erfasst als',
  'held with edits': 'mit Änderungen übernommen',
  ', and the distance between the two is what the run report learns from': ', und aus der Distanz zwischen beiden lernt der Laufbericht',
  'name': 'Name',
  'description': 'Beschreibung',
  'title': 'Titel',
  'expected result': 'erwartetes Ergebnis',
  'preconditions': 'Vorbedingungen',
  'test data reference': 'Testdaten-Referenz',
  'context': 'Kontext',
  'measurement method': 'Messmethode',
  'rationale': 'Begründung',
  'why you changed it (recorded on the proposal)': 'warum Sie es geändert haben (auf dem Vorschlag erfasst)',
  'Hold the amended text': 'Angepassten Text übernehmen',
  'Cancel': 'Abbrechen',
  'Refused because — the reason is the only signal the run report ever gets back':
    'Abgelehnt weil — der Grund ist das einzige Signal, das der Laufbericht je zurückerhält',
  'Duplicate of {ref}': 'Duplikat von {ref}',
  'Contradicts {ref}': 'Widerspricht {ref}',
  'note (optional) — recorded on the proposal beside the reason': 'Notiz (optional) — neben dem Grund auf dem Vorschlag erfasst',

  // ── refusal reasons (labels in src/client/api/proposals.ts) ────────────────
  'Duplicate': 'Duplikat',
  'Contradicts the register': 'Widerspricht dem Register',
  'Superseded by later evidence': 'Durch späteren Beleg ersetzt',
  'Not testable': 'Nicht prüfbar',
  'Not a requirement': 'Keine Anforderung',
  'Evidence does not support it': 'Beleg stützt es nicht',
  'Out of scope': 'Ausserhalb des Umfangs',
  'Other': 'Sonstiges',

  // ── stand empty states ─────────────────────────────────────────────────────
  'Nothing treated yet.': 'Noch nichts behandelt.',
  'Every claim on file carries a human disposition.': 'Jede erfasste Behauptung trägt einen menschlichen Entscheid.',
  'The {n} treated claim is on the other tab.': 'Die {n} behandelte Behauptung steht auf dem anderen Tab.',
  'The {n} treated claims are on the other tab.': 'Die {n} behandelten Behauptungen stehen auf dem anderen Tab.',
  'Hold or refuse a claim and it moves here, with its disposition and its undo.':
    'Übernehmen oder lehnen Sie eine Behauptung ab, wandert sie hierhin — mit ihrem Entscheid und ihrem Rückgängig.',
  'Show the {n} treated →': 'Die {n} behandelten anzeigen →',

  // ── zone 2 · this session ──────────────────────────────────────────────────
  'This session': 'Diese Sitzung',
  '{n} · newest first': '{n} · neueste zuerst',
  'Nothing disposed yet. Each disposition lands here with an undo, so a wrong hold is one click away for as long as this session lasts — not a filter change and a scan of':
    'Noch nichts entschieden. Jeder Entscheid landet hier mit einem Rückgängig; eine falsche Übernahme ist einen Klick entfernt, solange diese Sitzung dauert — kein Filterwechsel und kein Durchsuchen von',
  'held rows.': 'übernommenen Zeilen.',

  // ── zone 3 · the runs ──────────────────────────────────────────────────────
  'The runs behind this queue': 'Die Läufe hinter dieser Warteschlange',
  '{n} run with unheld claims': '{n} Lauf mit offenen Behauptungen',
  '{n} runs with unheld claims': '{n} Läufe mit offenen Behauptungen',
  'unheld of': 'offen von',
  'filed ·': 'eingereichten ·',
  'cited {covered} of the {n} document it was given': 'zitierte {covered} des {n} erhaltenen Dokuments',
  'cited {covered} of the {n} documents it was given': 'zitierte {covered} der {n} erhaltenen Dokumente',
  'cited {n} document': 'zitierte {n} Dokument',
  'cited {n} documents': 'zitierte {n} Dokumente',
  'read {n} register item': 'las {n} Registereintrag',
  'read {n} register items': 'las {n} Registereinträge',
  'no close discipline recorded': 'keine Abschlussdisziplin erfasst',
  '{n} tool rejection': '{n} Tool-Zurückweisung',
  '{n} tool rejections': '{n} Tool-Zurückweisungen',
  'read the evidence closely': 'lesen Sie die Belege genau',
  'clean tool log': 'sauberes Tool-Protokoll',
  '{n} turn': '{n} Runde',
  '{n} turns': '{n} Runden',
  'hide the account': 'den Bericht ausblenden',
  'what it says it did': 'was er getan haben will',
  'the collision check did not answer — a bulk hold cannot be offered blind':
    'die Kollisionsprüfung hat nicht geantwortet — eine Sammelübernahme kann nicht blind angeboten werden',
  'Hold all — check unavailable': 'Alle übernehmen — Prüfung nicht verfügbar',
  'a bulk control must not be able to commit a known collision': 'ein Sammel-Bedienelement darf keine bekannte Kollision festschreiben können',
  'Hold all — {n} collide': 'Alle übernehmen — {n} kollidieren',
  'Hold all {n}': 'Alle {n} übernehmen',
  'This run wrote no closing account. The close discipline is enforced at the tool layer':
    'Dieser Lauf hat keinen Abschlussbericht geschrieben. Die Abschlussdisziplin wird auf der Tool-Ebene durchgesetzt',
  'a run without one either predates it or ended before it could satisfy it.':
    'ein Lauf ohne Bericht ist entweder älter als sie oder endete, bevor er sie erfüllen konnte.',

  // ── the span sheet ─────────────────────────────────────────────────────────
  'the span in context': 'die Quellenstelle im Kontext',
  'Close': 'Schliessen',
  'The document could not be read': 'Das Dokument konnte nicht gelesen werden',
  'chars {start}–{end}': 'Zeichen {start}–{end}',
  'The quoted span is validated as a literal substring of {ref} at propose time and again on accept — an agent cannot file a quotation this document does not contain.':
    'Die zitierte Stelle wird beim Einreichen und erneut bei der Übernahme als wörtliche Teilzeichenkette von {ref} geprüft — ein Agent kann kein Zitat einreichen, das dieses Dokument nicht enthält.',

  // ── the two modals ─────────────────────────────────────────────────────────
  'Hold {n} claim': '{n} Behauptung übernehmen',
  'Hold {n} claims': '{n} Behauptungen übernehmen',
  'Hold them all': 'Alle übernehmen',
  'Every unheld claim in run #{run} will be held exactly as proposed, one at a time. Anything the register refuses (a baselined requirement, a failed evidence check) stays unheld and is reported back.':
    'Jede offene Behauptung in Lauf #{run} wird genau wie vorgeschlagen übernommen, eine nach der anderen. Was das Register ablehnt (eine Anforderung in der Baseline, eine fehlgeschlagene Belegprüfung), bleibt offen und wird zurückgemeldet.',
  'Delete claim #{id}': 'Behauptung #{id} löschen',
  'Deleting…': 'Wird gelöscht…',
  'Delete permanently': 'Endgültig löschen',
  'This removes the machine’s claim outright — it never reaches the register and it will NOT appear as refused with a reason, so the next pass cannot learn from it. The audit log keeps its payload. A claim worth an answer is refused, not deleted.':
    'Dies entfernt die Behauptung der Maschine vollständig — sie erreicht nie das Register und erscheint NICHT als abgelehnt mit Begründung; der nächste Lauf kann also nichts daraus lernen. Der Prüfpfad behält die Nutzdaten. Eine Behauptung, die eine Antwort verdient, wird abgelehnt, nicht gelöscht.',

  // ── toasts ─────────────────────────────────────────────────────────────────
  '#{id} held': '#{id} übernommen',
  '{ref} on the client-call agenda': '{ref} auf der Agenda des Kundengesprächs',
  'The hold failed — nothing was written.': 'Die Übernahme ist fehlgeschlagen — nichts wurde geschrieben.',
  '#{id} refused — {reason}': '#{id} abgelehnt — {reason}',
  'The refusal failed — nothing was written.': 'Die Ablehnung ist fehlgeschlagen — nichts wurde geschrieben.',
  'The undo was refused.': 'Das Rückgängigmachen wurde abgelehnt.',
  '{n} claim held': '{n} Behauptung übernommen',
  '{n} claims held': '{n} Behauptungen übernommen',
  '{n} refused and still unheld': '{n} abgelehnt und weiterhin offen',
  '#{id} deleted — the audit log keeps its payload': '#{id} gelöscht — der Prüfpfad behält die Nutzdaten',
  'The delete was refused.': 'Das Löschen wurde abgelehnt.',
  'Nothing collides with this claim — d only fires when the page is showing you what it duplicates.':
    'Nichts kollidiert mit dieser Behauptung — d greift nur, wenn die Seite Ihnen zeigt, was sie dupliziert.',
  'Amends {ref}':
    'Ändert {ref}',
  'This amendment names no field to change — refuse it.':
    'Diese Änderung nennt kein zu änderndes Feld – lehnen Sie sie ab.',
  'Why:':
    'Weshalb:',
  'Only the fields listed above change. Everything else on {ref} stays exactly as it is.':
    'Nur die oben aufgeführten Felder ändern sich. Alles andere an {ref} bleibt unverändert.',

  'deliverable amendment': 'Änderung Arbeitsergebnis',
  'content item amendment': 'Änderung Inhaltselement',
  'criterion amendment': 'Änderung Abnahmekriterium',
  'test case amendment': 'Änderung Testfall',
  'provisional mark': 'Vorbehalt',

  'the row this amendment changes':
    'die Zeile, die diese Änderung betrifft',
  'The record could not be read — hold this only after checking {ref} yourself.':
    'Der Datensatz konnte nicht gelesen werden – übernehmen Sie dies erst, nachdem Sie {ref} selbst geprüft haben.',
  '{ref} no longer exists — it was deleted after this was filed. Accepting will refuse; refuse it here instead.':
    '{ref} existiert nicht mehr – es wurde nach dem Einreichen gelöscht. Das Übernehmen wird abgelehnt; lehnen Sie es stattdessen hier ab.',
  'Read live from the register just now — not a copy taken when the proposal was filed.':
    'Soeben live aus dem Register gelesen – keine Kopie vom Zeitpunkt der Einreichung.',

  'now': 'bisher',
  'becomes': 'wird zu',
  'Unchanged — the same set, re-sent.':
    'Unverändert – dieselbe Menge, erneut gesendet.',
  'Unchanged — the same text, re-sent.':
    'Unverändert – derselbe Text, erneut gesendet.',
  'nothing': 'nichts',
  'Only those fields change. Everything else on {ref} stays exactly as it is.':
    'Nur diese Felder ändern sich. Alles andere an {ref} bleibt unverändert.',

  'Held as proposed — a list is not edited here.':
    'Wie vorgeschlagen übernommen – eine Liste wird hier nicht bearbeitet.',

}, 'ProposalsPage');
